/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Col, Row,
} from 'react-bootstrap';
import Table from 'components/Table';
import TextField from 'components/Form/TextField';
import { CellProps } from 'react-table';
import { DTable } from 'types/table';
import { IoIosAddCircleOutline } from 'react-icons/io';
import _ from 'lodash';
import moment from 'moment';
import { EventProps } from 'types/Event';
import EventDetailModal from 'components/Calendar/EventDetail';
import useFetchTableData from 'hooks/useFetchTableData';
import CreateEventModal from 'components/Modal/CreateEventModal';
import DeleteEventModal from 'components/Modal/DeleteEventModal';
import { AiOutlineDelete, AiOutlineEdit, AiOutlinePlus } from 'react-icons/ai';

function EventManagementView( {
  events, fetchData, isLoading, reloadData,
}:{
  reloadData: ()=>void;
  isLoading?:boolean;
  events:DTable;
  fetchData: ( params:any, pageSize:number )=>void ; } ) {
  const { t } = useTranslation();
  const [criteria, setCriteria] = useState( '' );
  const [showEventDetailModal, setEventDetailModal] = useState( false );
  const [showEditEventModal, setEditEventModal] = useState( false );
  const [showDeleteEventModal, setDeleteEventModal] = useState( false );
  const selectedEventRef = useRef<EventProps>();

  const closeEventDetailModal = useCallback( ( ) => {
    setEventDetailModal( false );
    selectedEventRef.current = undefined;
  }, [] );

  const openEventDetailModal = useCallback( ( event: EventProps ) => {
    selectedEventRef.current = event;
    setEventDetailModal( true );
  }, [] );

  const openEditEventModal = useCallback( ( event?: EventProps ) => {
    selectedEventRef.current = event;
    setEditEventModal( true );
  }, [] );

  const closeEditEventModal = useCallback( ( ) => {
    setEditEventModal( false );
    selectedEventRef.current = undefined;
  }, [] );

  const openDeleteEventModal = useCallback( ( event?: EventProps ) => {
    selectedEventRef.current = event;
    setDeleteEventModal( true );
  }, [] );

  const closeDeleteEventModal = useCallback( ( ) => {
    setDeleteEventModal( false );
    selectedEventRef.current = undefined;
  }, [] );

  const debounceSearch = useCallback( _.debounce( ( fn ) => fn(), 500 ), [] );

  const headers = [
    {
      Header: t( 'admin.event.header2' ),
      headerClassName: 'text--left',
      accessor: 'titulo',
      customWidth: 150,
    },
    {
      Header: t( 'admin.event.header3' ),
      headerClassName: 'text--left',
      accessor: 'promocion',
      customWidth: 150,
    },
    {
      Header: t( 'admin.event.header41' ),
      headerClassName: 'text--left',
      accessor: 'createdAt',
      customWidth: 150,
      Cell: ( { cell }: CellProps<any> ) => (
        <>
          {cell.value ? ( <span className="text--bold">{moment.utc( cell.value ).local().format( 'DD/MM/YYYY' )}</span> ) : null}
          <br />
          {cell.value ? ( <span className="text--bold">{moment.utc( cell.value ).local().format( 'HH:mm' )}</span> ) : null}
        </>
      ),
    },
    {
      Header: t( 'admin.event.header4' ),
      headerClassName: 'text--left',
      accessor: 'fechaInicio',
      customWidth: 150,
      Cell: ( { cell }: CellProps<any> ) => (
        <>
          {cell.value ? ( <span className="text--bold">{moment.utc( cell.value ).local().format( 'DD/MM/YYYY' )}</span> ) : null}
          <br />
          {cell.value ? ( <span className="text--bold">{moment.utc( cell.value ).local().format( 'HH:mm' )}</span> ) : null}
        </>
      ),
    },
    {
      Header: t( 'admin.event.header5' ),
      headerClassName: 'text--left',
      accessor: 'fechaFin',
      customWidth: 150,
      Cell: ( { cell }: CellProps<any> ) => (
        <>
          {cell.value ? ( <span className="text--bold">{moment.utc( cell.value ).local().format( 'DD/MM/YYYY' )}</span> ) : null}
          <br />
          {cell.value ? ( <span className="text--bold">{moment.utc( cell.value ).local().format( 'HH:mm' )}</span> ) : null}
        </>
      ),
    },
    {
      Header: t( 'admin.event.header6' ),
      headerClassName: 'text--left',
      accessor: 'allDate',
      customWidth: 150,
      Cell: ( { cell }: CellProps<any> ) => (
        <div>
          {cell.value ? 'SÍ' : 'NO'}
        </div>
      ),
    },
    {
      Header: t( 'admin.invitations.header7' ),
      accessor: 'item5',
      className: 'text--right',
      disableSortBy: true,
      customWidth: 80,
      Cell: ( { cell }:CellProps<any> ) => (
        <>
          <Button
            className="p-0"
            variant="link"
            onClick={() => {
              openEventDetailModal( cell.row.original );
            }}
          >
            <i className="icon-eye-line text-primary icon--xs" />
          </Button>
          <Button
            className="p-0 me-1"
            variant="link"
            onClick={() => {
              openEditEventModal( cell.row.original );
            }}
          >
            <AiOutlineEdit />
          </Button>
          <Button
            className="p-0"
            variant="link"
            onClick={() => {
              openDeleteEventModal( cell.row.original );
            }}
          >
            <AiOutlineDelete />
          </Button>
        </>
      ),
      headerClassName: 'text--right',
    },
  ];

  // const defaultSortBy = [{ id: 'fechaInicio', desc: true }];

  return (
    <>
      <Table
        columns={[...headers]}
        isLoading={isLoading}
        data={events.data}
        total={events.totalItems}
        pageCount={events.pageCount}
        fetchData={fetchData}
        // defaultSortBy={defaultSortBy}
        header={( filters, setFilters ) => (
          <Row className="align-items-center">
            <Col lg={9} sm={8} xs={6}>
              <Button
                size="sm"
                className="pt-2 pb-2 ps-4 pe-4 text--normal btn--rounded d-flex align-items-center"
                onClick={() => openEditEventModal()}
              >
                <IoIosAddCircleOutline size={24} className="me-2" />
                {t( 'admin.common.createEvent' )}
              </Button>
            </Col>
            <Col lg={3} sm={4} xs={6}>
              <TextField
                value={criteria}
                showErrors={false}
                placeholder="search"
                appendIcon="icon-search text-primary"
                onChange={( value ) => {
                  setCriteria( value );
                  debounceSearch( () => setFilters( ['nombre'], value ) );
                }}
                classNames={{ formGroup: 'input-filters' }}
              />
            </Col>
          </Row>
        )}
      />

      {showEventDetailModal && (
      <EventDetailModal
        data={selectedEventRef.current}
        onClose={closeEventDetailModal}
      />
      )}

      {showEditEventModal && (
      <CreateEventModal
        data={selectedEventRef.current}
        onClose={closeEditEventModal}
        reloadData={reloadData}
      />
      )}

      {showDeleteEventModal && (
      <DeleteEventModal
        eventId={selectedEventRef.current?.eventoId as number}
        onClose={closeDeleteEventModal}
        reloadData={reloadData}
      />
      )}
    </>
  );
}

export default EventManagementView;
